import { Classes, Intent, MaybeElement } from "@blueprintjs/core";
import React, { useMemo } from "react";
import styled, { Interpolation, css } from "styled-components";

import {
  HotkeyHelperTooltip,
  HotkeyHelperTooltipProps,
} from "../components/common/HotkeyHelperTooltip.js";
import { SingleChevronDownIcon } from "../components/icons/CustomIcons";
import { TestSelectors } from "../util/testSelectors.js";

import { HexButton, HexButtonGroup } from "./HexButton.js";
import { HexPopover, HexPopoverProps } from "./HexPopover.js";

const StyledHexButtonGroup = styled(HexButtonGroup)`
  flex: none;
`;

// Shrink icon gap if small or extraSmall
const StyledPrimaryButton = styled(HexButton)<{
  extraSmall?: boolean;
  small?: boolean;
}>`
  &&&& {
    ${({ extraSmall, small }) =>
      (extraSmall || small) &&
      css`
        .${Classes.ICON} {
          margin-right: 4px;
        }
      `}
  }
`;

interface PrimaryButtonProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "content"
> {
  altFocusRing?: boolean;
  dataTest?: TestSelectors;
  disabled?: boolean;
  icon?: MaybeElement;
  intent?: Intent;
  loading?: boolean;
  fill?: boolean;
  onClick?: (evt: React.MouseEvent<HTMLElement>) => void;
  content?: string | MaybeElement;
  extraSmall?: boolean;
  subtle: boolean;
  small?: boolean;
  customCSS?: Interpolation<object>;
  minimal?: boolean;
  rightIcon?: MaybeElement;
}

const PrimaryButton = React.memo(
  React.forwardRef<HTMLButtonElement, PrimaryButtonProps>(
    function PrimaryButton(
      {
        altFocusRing,
        content,
        customCSS,
        dataTest,
        disabled,
        extraSmall,
        fill,
        icon,
        intent,
        loading,
        minimal,
        onClick,
        rightIcon,
        small,
        subtle,
        ...rest
      },
      ref,
    ) {
      return (
        <StyledPrimaryButton
          ref={ref}
          altFocusRing={altFocusRing}
          css={customCSS}
          data-test={dataTest}
          disabled={disabled}
          extraSmall={extraSmall}
          fill={fill}
          icon={icon}
          intent={intent}
          loading={loading}
          minimal={minimal}
          rightIcon={rightIcon}
          small={small}
          subtle={subtle}
          onClick={onClick}
          {...rest}
        >
          {content}
        </StyledPrimaryButton>
      );
    },
  ),
);

type TooltipProps = Omit<HotkeyHelperTooltipProps, "children">;

export interface HexButtonWithDropdownProps {
  className?: string;
  dataTest?: TestSelectors;
  // Disables BOTH the button AND the popover
  disabled?: boolean;
  icon?: MaybeElement;
  intent?: Intent;
  onClick?: (evt: React.MouseEvent<HTMLElement>) => void;
  buttonContent?: string | MaybeElement;
  loading?: boolean;
  popoverContent?: string | JSX.Element;
  popoverDisabled?: boolean;
  fill?: boolean;
  tooltip?: TooltipProps;
  buttonDisabled?: boolean;
  extraSmall?: boolean;
  subtle?: boolean;
  small?: boolean;
  large?: boolean;
  customCSS?: Interpolation<object>;
  buttonGroupCSS?: Interpolation<object>;
  dropdownButtonCSS?: Interpolation<object>;
  minimal?: boolean;
  hideDropdown?: boolean;
  usePortal?: boolean;
  primaryButtonDataTest?: TestSelectors;
  dropdownButtonDataTest?: TestSelectors;
  rightIcon?: MaybeElement;
  altFocusRing?: boolean;
  popoverProps?: Partial<HexPopoverProps>;
  primaryButtonProps?: React.HTMLAttributes<HTMLElement> & {
    ref?: React.Ref<HTMLButtonElement>;
  };
}

export const HexButtonWithDropdown = React.memo(
  React.forwardRef<HTMLDivElement, HexButtonWithDropdownProps>(
    function HexButtonWithDropdown(
      {
        altFocusRing = false,
        buttonContent,
        buttonDisabled,
        buttonGroupCSS,
        customCSS,
        dataTest,
        disabled,
        dropdownButtonCSS,
        dropdownButtonDataTest,
        extraSmall = false,
        fill,
        hideDropdown = false,
        icon,
        intent,
        loading,
        minimal,
        onClick,
        popoverContent,
        popoverDisabled,
        popoverProps,
        primaryButtonDataTest,
        primaryButtonProps,
        rightIcon,
        small = true,
        subtle = true,
        tooltip,
        usePortal = true,
        ...rest
      },
      ref,
    ) {
      const primaryButton = useMemo(() => {
        return (
          <PrimaryButton
            altFocusRing={altFocusRing}
            content={buttonContent}
            customCSS={customCSS}
            dataTest={
              primaryButtonDataTest ??
              TestSelectors.HEX_BUTTON_WITH_DROP_PRIMARY_BUTTON
            }
            disabled={disabled || buttonDisabled}
            extraSmall={extraSmall}
            fill={fill}
            icon={icon}
            intent={intent}
            loading={loading}
            minimal={minimal}
            rightIcon={rightIcon}
            small={small}
            subtle={subtle}
            onClick={onClick}
            {...primaryButtonProps}
          />
        );
      }, [
        altFocusRing,
        buttonContent,
        buttonDisabled,
        customCSS,
        disabled,
        extraSmall,
        fill,
        icon,
        intent,
        loading,
        minimal,
        onClick,
        primaryButtonDataTest,
        primaryButtonProps,
        rightIcon,
        small,
        subtle,
      ]);

      const primaryButtonContents = useMemo(() => {
        if (tooltip) {
          return (
            <HotkeyHelperTooltip
              combo={tooltip.combo}
              disabled={tooltip.disabled}
              hotkeyName={tooltip.hotkeyName}
              placement="bottom"
            >
              {primaryButton}
            </HotkeyHelperTooltip>
          );
        } else {
          return primaryButton;
        }
      }, [primaryButton, tooltip]);

      return (
        <StyledHexButtonGroup
          ref={ref}
          css={buttonGroupCSS}
          data-test={dataTest}
          fill={fill}
          minimal={minimal}
          {...rest}
        >
          {primaryButtonContents}
          {!hideDropdown && (
            <HexPopover
              {...popoverProps}
              content={popoverContent}
              css="max-width: 28px;" // max-width to allow the button to fill the parent
              disabled={disabled || popoverDisabled}
              minimal={true}
              placement="bottom-end"
              // eslint-disable-next-line react/jsx-no-bind -- renderTarget
              renderTarget={({ isOpen: targetIsOpen, ...targetProps }) => (
                <HexDropdownButton
                  active={targetIsOpen}
                  {...targetProps}
                  $forceCornerRadius={
                    /* when a portal isn't used, the popover content gets placed
                  as a peer node, causing this button to square off its corners
                  unless we explicitly tell it not to do so */
                    !usePortal
                  }
                  altFocusRing={altFocusRing}
                  css={dropdownButtonCSS}
                  data-test={
                    dropdownButtonDataTest ??
                    TestSelectors.HEX_BUTTON_WITH_DROP_CHEVRON_BUTTON
                  }
                  disabled={disabled || popoverDisabled}
                  extraSmall={extraSmall}
                  icon={
                    <SingleChevronDownIcon
                      css={{
                        opacity: subtle ? 0.6 : 1,
                      }}
                      iconSize={12}
                    />
                  }
                  intent={intent}
                  small={small}
                  subtle={subtle}
                />
              )}
              usePortal={usePortal}
            />
          )}
        </StyledHexButtonGroup>
      );
    },
  ),
);

export const HexDropdownButton = styled(HexButton)<{
  $forceCornerRadius: boolean;
}>`
  ${({ $forceCornerRadius }) =>
    $forceCornerRadius &&
    css`
      border-top-right-radius: ${({ theme }) => theme.borderRadius} !important;
      border-bottom-right-radius: ${({ theme }) =>
        theme.borderRadius} !important;
    `}
`;
